import { useEffect, useRef, useState, useCallback } from "react";
import usePageLifecycleEvents from "./usePageLifecycleEvents";

/**
 * usePolling - Reusable hook for background polling/auto-refresh
 *
 * Features:
 * - Configurable interval
 * - Optional pause when tab is hidden (Page Visibility API)
 * - Automatic cleanup
 * - Can be enabled/disabled
 * - Exponential backoff on consecutive failures
 * - Auto-stop after excessive failures
 *
 * @param {function} callback - Function to call on each interval
 * @param {number} intervalMs - Polling interval in milliseconds
 * @param {Object} options - Configuration options
 * @param {boolean} options.enabled - Enable/disable polling (default: true)
 * @param {boolean} options.pauseWhenHidden - Pause polling when tab is hidden (default: true)
 * @param {boolean} options.runOnMount - Run callback immediately on mount (default: false)
 *
 * @example
 * usePolling(() => fetchData(true), 60000, {
 *   enabled: true,
 *   pauseWhenHidden: true
 * });
 */
function usePolling(callback, intervalMs, options = {}) {
  const {
    enabled = true,
    pauseWhenHidden = true,
    runOnMount = false,
  } = options;

  const savedCallback = useRef(callback);
  const intervalId = useRef(null);

  // Error tracking for exponential backoff
  const [, setConsecutiveErrors] = useState(0);
  const [backoffMultiplier, setBackoffMultiplier] = useState(1);
  const effectiveInterval = intervalMs * backoffMultiplier;

  // Keep the effective interval in a ref so the resume handler can re-arm the
  // interval with the current value without capturing a stale closure.
  const effectiveIntervalRef = useRef(effectiveInterval);
  useEffect(() => {
    effectiveIntervalRef.current = effectiveInterval;
  }, [effectiveInterval]);

  // Keep callback reference fresh
  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  // Wrapped callback with error tracking and exponential backoff
  const wrappedCallback = useCallback(async () => {
    try {
      await savedCallback.current();

      // Success - reset backoff (use functional updater to avoid stale closure)
      setConsecutiveErrors((prev) => {
        if (prev > 0) {
          setBackoffMultiplier(1);
        }
        return 0;
      });
    } catch (err) {
      // Track consecutive errors
      setConsecutiveErrors((prev) => {
        const newCount = prev + 1;

        // Start exponential backoff after 3 failures
        if (newCount >= 3) {
          setBackoffMultiplier((prevMultiplier) =>
            Math.min(prevMultiplier * 2, 8),
          );
        }

        // Stop polling after 10 consecutive failures
        if (newCount >= 10) {
          console.warn(
            `[usePolling] Stopping due to ${newCount} consecutive failures`,
          );
          if (intervalId.current) {
            clearInterval(intervalId.current);
            intervalId.current = null;
          }
        }

        return newCount;
      });

      throw err; // Re-throw for caller to handle
    }
  }, []);

  // Resume handler shared by the Page Visibility API path and the mobile
  // lifecycle events (pageshow/focus/online). Restores polling after the page
  // has been suspended or restored from bfcache:
  //   (a) reset stale backoff state from a prior suspended session,
  //   (b) re-arm the interval (including after the 10-failure auto-stop),
  //   (c) fire an immediate refresh for instant feedback.
  // Reads effectiveIntervalRef so re-arming is never throttled by a stale
  // closure even though resetting backoff re-renders and re-runs the effect.
  // A shared cooldown ref dedups the visibilitychange path against the helper
  // events so the resume burst fires exactly one fetch.
  const lastResumeRef = useRef(0);
  const RESUME_COOLDOWN_MS = 1000;
  const handleResume = useCallback(() => {
    const now = Date.now();
    if (now - lastResumeRef.current < RESUME_COOLDOWN_MS) {
      return;
    }
    lastResumeRef.current = now;

    setConsecutiveErrors(0);
    setBackoffMultiplier(1);

    // Immediate refresh on return.
    wrappedCallback().catch((err) => {
      console.error("[usePolling] Immediate refresh error:", err);
    });

    // Re-arm the interval if it was cleared (hidden tab or auto-stop). Mirror
    // the existing guard so we never create a second interval.
    if (!intervalId.current) {
      intervalId.current = setInterval(() => {
        wrappedCallback().catch((err) => {
          console.error("[usePolling] Polling error:", err);
        });
      }, effectiveIntervalRef.current);
    }
  }, [wrappedCallback]);

  // Mobile lifecycle resilience: pageshow (bfcache), focus, and online. The
  // helper's cooldown dedups the overlapping resume burst, including the
  // visibilitychange path below, so we never double-fetch on return.
  usePageLifecycleEvents(handleResume, {
    enabled: enabled && pauseWhenHidden,
  });

  // Run callback on mount if requested
  useEffect(() => {
    if (runOnMount && enabled) {
      wrappedCallback().catch((err) => {
        console.error("[usePolling] Run-on-mount error:", err);
      });
    }
  }, [runOnMount, enabled, wrappedCallback]);

  // Setup polling
  useEffect(() => {
    if (!enabled) return;

    // Start interval with effective interval (includes backoff)
    intervalId.current = setInterval(() => {
      wrappedCallback().catch((err) => {
        console.error("[usePolling] Polling error:", err);
      });
    }, effectiveInterval);

    // Optional: Pause when tab is hidden
    if (pauseWhenHidden) {
      const handleVisibilityChange = () => {
        if (document.hidden) {
          // Tab hidden - clear interval
          if (intervalId.current) {
            clearInterval(intervalId.current);
            intervalId.current = null;
          }
        } else {
          // Tab visible - resume via the shared, cooldown-guarded path so the
          // immediate refresh and interval re-arm don't double-fire when
          // pageshow/focus also land in the same resume burst.
          handleResume();
        }
      };

      document.addEventListener("visibilitychange", handleVisibilityChange);

      return () => {
        if (intervalId.current) {
          clearInterval(intervalId.current);
          intervalId.current = null;
        }
        document.removeEventListener(
          "visibilitychange",
          handleVisibilityChange,
        );
      };
    }

    // Cleanup interval
    return () => {
      if (intervalId.current) {
        clearInterval(intervalId.current);
        intervalId.current = null;
      }
    };
  }, [effectiveInterval, enabled, pauseWhenHidden, wrappedCallback, handleResume]);
}

export default usePolling;
